import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  VStack, 
  Heading, 
  Text, 
  useToast, 
  Flex,
  Icon,
} from '@chakra-ui/react';
import { FiArrowLeft, FiMail } from 'react-icons/fi';
import api from '../services/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const toast = useToast();

  const sendResetEmail = async (emailToSend) => {
    try {
      const { data } = await api.post('/api/auth/request-password-reset', { 
        email: emailToSend.toLowerCase().trim()
      });
      setEmailSent(true);
      return data;
    } catch (error) {
      setEmailSent(true);
      console.error('Password reset request failed:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const data = await sendResetEmail(email);
    
    toast({
      title: 'Email sent',
      description: data?.message || 'Check your email for the password reset link',
      status: 'success',
      duration: 7000,
      isClosable: true,
    });
    
    setIsLoading(false);
  };

  const handleResend = async () => {
    setIsResending(true);
    
    const data = await sendResetEmail(email);
    
    toast({
      title: 'Email resent',
      description: data?.message || 'Another reset link has been sent to your email',
      status: 'success',
      duration: 7000,
      isClosable: true,
    });
    
    setIsResending(false);
  };

  return (
    <Flex align="center" justify="center" minH="100vh" bg="#0a0a0a">
      <Box 
        maxW="md" 
        w="100%" 
        p={8}
        borderRadius="12px"
        border="1px solid rgba(255,255,255,0.10)"
        bg="#0f0f0f"
      >
        <VStack spacing={6}>
          <Box mb={2}>
            <img 
              src="/gillyreach-favicon.png" 
              alt="GillyReach" 
              style={{ height: '52px', width: 'auto', margin: '0 auto' }}
            />
          </Box>
          <Heading
            size="lg"
            color="#fafafa"
            fontWeight="700"
            letterSpacing="-0.02em"
          >
            Reset password
          </Heading>

          {!emailSent ? (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <VStack spacing={4} align="stretch">
                <FormControl id="email" isRequired>
                  <FormLabel fontSize="sm" color="#a3a3a3" mb={1.5}>Email Address</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    bg="#171717"
                    borderColor="rgba(255,255,255,0.12)"
                    color="#fafafa"
                    fontSize="sm"
                    _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                    _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  bg="#fafafa"
                  color="#0a0a0a"
                  borderRadius="full"
                  fontWeight="600"
                  fontSize="sm"
                  h="40px"
                  width="full"
                  mt={2}
                  isLoading={isLoading}
                  loadingText="Sending..."
                  _hover={{ opacity: 0.85 }}
                >
                  Send reset link
                </Button>
              </VStack>
            </form>
          ) : (
            <Box
              w="100%"
              p={5}
              borderRadius="10px"
              bg="rgba(34,197,94,0.05)"
              border="1px solid rgba(34,197,94,0.2)"
              textAlign="center"
            >
              <VStack spacing={3}>
                <Icon as={FiMail} boxSize={8} color="#4ade80" />
                <Text fontSize="sm" fontWeight="600" color="#fafafa">
                  Check your email
                </Text>
                <Text fontSize="sm" color="#a3a3a3" lineHeight="1.7">
                  We&apos;ve sent a reset link to <Text as="span" color="#fafafa">{email}</Text>.
                </Text>
                <Text fontSize="xs" color="rgba(255,255,255,0.3)">
                  Don&apos;t see it? Check your spam folder.
                </Text>
              </VStack>
            </Box>
          )}

          <VStack spacing={2} w="100%">
            <Link to="/login" style={{ width: '100%' }}>
              <Button
                leftIcon={<Icon as={FiArrowLeft} boxSize={3.5} />}
                variant="ghost"
                width="full"
                fontSize="sm"
                color="rgba(255,255,255,0.5)"
                borderRadius="full"
                _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.05)' }}
              >
                Back to sign in
              </Button>
            </Link>

            {emailSent && (
              <Button
                variant="ghost"
                size="sm"
                color="rgba(255,255,255,0.3)"
                borderRadius="full"
                onClick={handleResend}
                isLoading={isResending}
                loadingText="Resending..."
                _hover={{ color: 'rgba(255,255,255,0.6)', bg: 'rgba(255,255,255,0.05)' }}
              >
                Resend email
              </Button>
            )}
          </VStack>
        </VStack>
      </Box>
    </Flex>
  );
};

export default ForgotPasswordPage;
